import React from 'react';

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import Badge from 'reactstrap/lib/Badge';
import Button from 'reactstrap/lib/Button';
import Col from 'reactstrap/lib/Col';
import Row from 'reactstrap/lib/Row';
import Table from 'reactstrap/lib/Table';
import Alert from 'reactstrap/lib/Alert';

import { OrderProcessStatus } from '@ttstr/api/orders';
import { revokeVoucherSwap } from '@ttstr/api';
import { requestOrder } from '@ttstr/actions';
import { LoadingSpinner, useShopConfig, ContentrArea } from '@ttstr/components';
import Spinner from 'reactstrap/lib/Spinner';
import Address from '@ttstr/components/Address/Address';
import CartListingRow from '@ttstr/components/ShoppingCart/CartListingRow';
import Currency from '@ttstr/components/Intl/Currency';
import CustomerFromOrder from '@ttstr/components/Order/CustomerFromOrder';
import DateComponent from '@ttstr/components/Intl/DateComponent';
import Percent from '@ttstr/components/Intl/Percent';
import { useShallowEqualSelector, useActions } from '@ttstr/utils';
import { AppState } from '@ttstr/reducers';
import { getOrder } from '@ttstr/reducers/orders';

import PaymentInfo from './PaymentInfo';
import C19Personalization from './C19Personalization';

interface OwnProps {
  orderId: string;
  xtoken?: string;
  closeIcon?: React.ReactNode;
}

type Props = Readonly<OwnProps>;

const pp2RetryTimeout = 20_000; // 20 seconds
const maxPp2Retries = 2;
let pp2RetryCount = 0;

const OrderDetails: React.FC<Props> = ({ orderId, xtoken, closeIcon }) => {
  const { t } = useTranslation();
  const shopConfig = useShopConfig();

  const [loading, setLoading] = React.useState(false);
  const [pp2Loading, setPp2Loading] = React.useState(false);
  const { order } = useShallowEqualSelector((state: AppState) => {
    return {
      order: getOrder(state.Orders.orders, Number(orderId)),
    };
  });
  const { requestOrder } = useActions(mapDispatchToProps);

  const [paypalError, setPaypalError] = React.useState<Error>(null);
  const [paymentAction, setPaymentAction] = React.useState<string>(null);

  React.useEffect(() => {
    (async () => {
      if (!orderId) return;
      try {
        setLoading(true);
        await requestOrder(Number(orderId), xtoken);
      } finally {
        setLoading(false);
      }
    })();
  }, [orderId, xtoken]);

  React.useEffect(() => {
    if (
      order &&
      'payment_errors' in order &&
      Array.isArray(order.payment_errors) &&
      order.payment_errors.includes('pay_pal_issue')
    ) {
      setPaypalError(new Error('pay_pal_issue'));
      return;
    }

    if (
      order?.payment_method_sid === 'paypal2' &&
      order.payment_status !== OrderProcessStatus.done &&
      typeof order.payment_action === 'string' &&
      order.payment_action.length > 0
    ) {
      setPaymentAction(order.payment_action);
      return;
    }

    if (
      order?.payment_method_sid === 'paypal2' &&
      order.payment_status !== OrderProcessStatus.done &&
      pp2RetryCount++ <= maxPp2Retries
    ) {
      setPp2Loading(true);
      const timeoutId = setTimeout(() => {
        requestOrder(Number(orderId), xtoken); /* console.log("pp2 done? ", pp2RetryCount);*/
      }, pp2RetryTimeout);
      return () => {
        clearTimeout(timeoutId); /* console.log("clear");*/
      };
    } else {
      setPp2Loading(false);
    }
  }, [order, orderId, xtoken, requestOrder]);

  const revoke = async (path: string) => {
    try {
      setLoading(true);
      await revokeVoucherSwap(path);
      await requestOrder(Number(orderId), xtoken);
    } finally {
      setLoading(false);
    }
  };

  const walletFiles = React.useMemo(() => {
    if (loading) return [];
    if (!order) return [];
    return order.vouchers
      .map((v) => {
        return v.pkpass_file
          ? {
              path: v.pkpass_file,
              cancelled: v.cancelled,
            }
          : null;
      })
      .filter(Boolean);
  }, [loading, order]);

  if (loading) return <LoadingSpinner label={t('LOADING.DATA')} />;
  if (!order) return <p className="text-danger">{t('ORDERS.DETAIL.ERROR')}</p>;

  return (
    <div className="order-details">
      <ContentrArea id="above-order-details" />
      <Row>
        <Col lg="4">
          <fieldset>
            <legend>
              {t('CUSTOMER.DATA')}
              <i className="fal fa-user ml-2 text-muted" />
            </legend>
            <Address {...order} />
          </fieldset>
          {order.use_shipping_address && (
            <fieldset>
              <legend>
                {t('CUSTOMER.SHIPPING_ADDRESS')}
                <i className="fal fa-shipping-fast ml-2 text-muted" />
              </legend>
              <Address
                firstname={order.shipping_firstname}
                lastname={order.shipping_lastname}
                companyname={order.shipping_companyname}
                street={order.shipping_street}
                address_addition={order.shipping_address_addition}
                state={order.shipping_state}
                zip_code={order.shipping_zip_code}
                city={order.shipping_city}
                country={order.shipping_country}
              />
            </fieldset>
          )}
          {order.registerable && (
            <CustomerFromOrder
              orderId={order.id}
              xtoken={order.simple_token}
              email={order.email}
              color="warning"
              closeIcon={closeIcon}
            />
          )}
        </Col>
        <Col lg="8">
          {paypalError && (
            <Alert color="danger" className="text-left">
              <p>{t('CART.PAYPAL_ERROR.MESSAGE')}</p>
              {/* <Label htmlFor="reference-id">{t('CART.CONVERSION.REFERENCE_ID')}</Label>
              <Input id="reference-id" type="textarea" defaultValue={cartToken} /> */}
            </Alert>
          )}
          {paymentAction && (
            <Alert color="warning" className="text-left">
              <p>{t('CART.PAYMENT_ACTION.MESSAGE')}</p>
              {/* <Label htmlFor="reference-id">{t('CART.CONVERSION.REFERENCE_ID')}</Label>
              <Input id="reference-id" type="textarea" defaultValue={cartToken} /> */}
              <Button onClick={() => window.open(order.payment_action, '_blank')}>
                {t('CART.PAYMENT_ACTION.ACTION')}
              </Button>
            </Alert>
          )}
          {order.payment_status !== OrderProcessStatus.done && order.payment_method_info && (
            <PaymentInfo info={order.payment_method_info} color="warning" />
          )}
          <fieldset>
            <legend>{t('ORDERS.DETAIL.OVERVIEW')}</legend>
            <Table striped hover className="tt-listing" responsive>
              <thead>
                <tr>
                  <th colSpan={2}>{t('ORDERS.DETAIL.ORDER_NO', order)}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    {t('ORDERS.DETAIL.ORDERED_AT')}{' '}
                    <strong>
                      <DateComponent value={order.ordered_at} />
                    </strong>
                  </td>
                  <td>
                    {t('ORDERS.DETAIL.VALUE')}{' '}
                    <strong>
                      <Currency value={order.value} />
                    </strong>
                  </td>
                </tr>
                <tr>
                  <td>
                    {t('ORDERS.DETAIL.PAID_PER')} <strong>{order.payment_method}</strong>
                  </td>
                  <td>
                    {pp2Loading ? (
                      <Spinner size="sm" color="primary" />
                    ) : (
                      // <LoadingSpinner label={t('LOADING.DATA')} />
                      <Badge color={statusToColor(order.payment_status)}>
                        {t(`ORDERS.DETAIL.STATUS.${order.payment_status.toUpperCase()}`)}
                        {<> {t('ORDERS.DETAIL.OPEN_AMOUNT', { amount: order.balance })}</>}
                      </Badge>
                    )}
                    {order.needs_online_payment && (
                      <Button
                        tag={Link}
                        to={`/cart/${order.cart_token}/status`}
                        type="button"
                        color="primary"
                        size="sm"
                        className="ml-2"
                      >
                        {t('ORDERS.DETAIL.COMPLETE_PAYMENT')}
                      </Button>
                    )}
                  </td>
                </tr>
                <tr>
                  <td>
                    {t('ORDERS.DETAIL.SHIPPED_PER')} <strong>{order.shipping_method}</strong>
                  </td>
                  <td>
                    <Badge color={statusToColor(order.shipping_status)}>
                      {t(`ORDERS.DETAIL.STATUS.${order.shipping_status.toUpperCase()}`)}
                    </Badge>
                  </td>
                </tr>
              </tbody>
            </Table>
          </fieldset>

          {/* TODO: text in locales */}
          <Table striped hover className="tt-listing" responsive>
            <tbody>
              {order.personalization && order.cancelled !== true && (
                <tr>
                  <th scope="row">{t('ORDERS.DETAIL.PERSONALIZATION.PERSONALIZED_FOR')}</th>
                  <td>
                    {/* wenn all_done, kann nicht mehr repersonalisiert werden */}
                    {order.personalization.all_done && !Boolean(shopConfig.alwaysAllowRepersonalization) ? (
                      <>
                        <Button color="secondary" disabled>
                          {t('ORDERS.DETAIL.PERSONALIZATION.CANT_BE_CHANGED')}
                        </Button>
                        {order.new_voucher_swap_path && (
                          <Alert color="danger" className="mt-3 mb-0">
                            {'Um die Personalisierung zu ändern, benutze bitte '}
                            <a href={order.new_voucher_swap_path} target="_blank" rel="noopener noreferrer">
                              {'unser Weiterverkaufs-Tool'}
                            </a>
                            {', um die Tickets an den/die neuen KarteninhaberIn zu übertragen.'}
                            <br />
                            {
                              'Löse deinen Weiterverkaufscode ganz einfach nach der Erstellung selbst ein und personalisiere deine neuen Tickets auf die gewünschten Namen.'
                            }
                          </Alert>
                        )}
                      </>
                    ) : (
                      <C19Personalization orderId={orderId} xtoken={xtoken} {...order.personalization} />
                    )}
                  </td>
                </tr>
              )}
              {(order.voucher_file ||
                Boolean(walletFiles.length) ||
                (order.shipping_method_sid === 'online' &&
                  (!order.personalization || order.personalization.all_done))) && (
                <tr>
                  <th scope="row">{t('ORDERS.DETAIL.TICKETS')}</th>
                  <td>
                    {/* {!order.voucher_file && !walletFiles.length && (
                      <Badge color="secondary">{t('ORDERS.DETAIL.NO_TICKETS_GENERATED')}</Badge>
                    )} */}
                    <ul className="list-unstyled mb-0">
                      {order.voucher_file ? (
                        <li className="mb-2">
                          <Button
                            color="secondary"
                            block
                            tag="a"
                            href={order.voucher_file}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <i className="fal fa-file-pdf" /> {t('ORDERS.DETAIL.TICKETS_DOWNLOAD')}
                          </Button>
                        </li>
                      ) : (
                        <li className="mb-2">
                          <span className="spinner-wrapper">
                            <Spinner color="primary" size="sm" />
                          </span>
                          &nbsp; Die Tickets werden generiert...
                        </li>
                      )}
                      {Boolean(walletFiles.length) &&
                        walletFiles.map((file, index) => (
                          <li key={index} className="mb-2">
                            <Button
                              color="secondary"
                              block
                              tag="a"
                              href={file.path}
                              disabled={file.cancelled}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <i className="fal fa-ticket" />{' '}
                              {file.cancelled ? (
                                <del>{t('ORDERS.DETAIL.WALLET_DOWNLOAD', { number: index + 1 })}</del>
                              ) : (
                                t('ORDERS.DETAIL.WALLET_DOWNLOAD', { number: index + 1 })
                              )}
                            </Button>
                          </li>
                        ))}
                    </ul>
                  </td>
                </tr>
              )}
              {Boolean(order.bought_coupons.length) && (
                <tr>
                  <th scope="row">{t('ORDERS.DETAIL.COUPONS')}</th>
                  <td>
                    <ul className="list-unstyled mb-0">
                      {order.bought_coupons.map((coupon, index) => (
                        <li key={index} className="mb-2">
                          <Button
                            color="secondary"
                            block
                            tag="a"
                            href={coupon.download}
                            target="_blank"
                            rel="noopener noreferrer"
                            download={`coupon-${coupon.token}`}
                          >
                            <i className="fal fa-file-pdf" /> {t('ORDERS.DETAIL.COUPONS_DOWNLOAD')}
                          </Button>
                        </li>
                      ))}
                    </ul>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          <Table striped hover className="tt-listing" responsive>
            <thead>
              <tr>
                <th scope="col">{t('CART.ITEMS')}</th>
                <th className="text-center" scope="col">
                  {/* eslint-disable-line react/jsx-no-literals */}
                  &nbsp;
                </th>
                <th className="text-right" scope="col">
                  {t('CART.BREAKDOWN')}
                </th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((item) => (
                <CartListingRow key={item.id} item={item} readOnly />
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th colSpan={2} className="text-right" scope="row">
                  {t(`CART.SUM`)}
                </th>
                <td className="text-right">
                  <strong>
                    <Currency value={order.value} />
                  </strong>
                </td>
              </tr>
              {Object.entries(order.vat_rates).map(([rate, value]) => (
                <tr key={rate} className="small text-muted">
                  <td colSpan={2} className="text-right">
                    {t('CART.VAT')} <Percent value={Number(rate) / 100} />
                  </td>
                  <td className="text-right">
                    <Currency value={Number(value)} />
                  </td>
                </tr>
              ))}
            </tfoot>
          </Table>

          {order.new_voucher_swap_path && (
            <fieldset>
              <legend>{'Tickets verkaufen'}</legend>
              <p>
                {`Du kannst die Codes mit deinen Freunden oder in Sozialen Netzwerken teilen.
                  Mit dem Code erhalten andere die Möglichkeit dein Ticket zu erwerben.
                  Der Käufer zahlt direkt an den Ticketshop, wodurch kein Geldtransfer zwischen dir und dem Käufer notwendig ist.`}
                <br />
                <br />
                {'Der Code kann unter '}
                <a href={'/swp'} target="_blank" rel="noopener noreferrer">{`${location.host}/swp`}</a>
                {' eingelöst werden.'}
              </p>
              <p>
                <Button
                  color="secondary"
                  // block
                  tag="a"
                  href={order.new_voucher_swap_path}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {'Tickets zum Weiterverkauf anbieten'}
                </Button>
              </p>
              <Table striped hover className="tt-listing" responsive>
                <tbody>
                  {order.items
                    .map((i) => i.voucher_swaps)
                    .flat()
                    .filter((i) => i)
                    .map((swap) => (
                      <tr key={swap.token}>
                        <td>
                          {swap.status === 'sold' ? (
                            <s>
                              <span className="swap-token">{swap.token}</span>
                            </s>
                          ) : (
                            <span className="swap-token">{swap.token}</span>
                          )}{' '}
                          {swap.status === 'carted' && <i className="fal fa-shopping-cart" />}
                          {(swap.status === 'paid' || swap.status === 'sold') && (
                            <i className="fal fa-money-bill-wave" />
                          )}
                          {swap.status === 'sold' && ' ' && <i className="fal fa-check" />}
                          {swap.status === 'new' && (
                            <Button
                              color="link"
                              className="float-right"
                              size="sm"
                              // block
                              tag="button"
                              onClick={() => revoke(swap.revoke_path)}
                            >
                              {'Angebot zurückziehen'}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </fieldset>
          )}
        </Col>
      </Row>
    </div>
  );
};

function statusToColor(status: string) {
  switch (status) {
    case 'failure':
      return 'danger';
    case 'pending':
      return 'warning';
    case 'done':
      return 'success';
    case 'hold':
      return 'dark';
    case 'unapproved': // fall-through
    case 'processing': // fall-through
    case 'new': // fall-through
    default:
      return 'secondary';
  }
}

const mapDispatchToProps = {
  requestOrder,
};

export default React.memo(OrderDetails);
